import type { ListingAgentPhoneType } from "@/api/buyers";
import type { ListingAgentPasteCandidateDto } from "@/api/listingAgentPaste";
import { US_STATES } from "@/lib/usStates";
import type { AgentForm } from "../../ListingAgentDirectory";

/**
 * PASTE TO FILL's merge rule (D5) — pure, so every case is pinned without a
 * render.
 *
 * <p>🔴 <b>Fill blanks only.</b> A field the specialist typed is never
 * overwritten: when the paste disagrees it becomes a CONFLICT that shows both
 * values and lets them pick. Phone type fills only while the form's is
 * "Unknown", and only for the phone the form actually ends up with.</p>
 *
 * <p>🔴 <b>The person's details go to the contact fields, never Notes</b> (the
 * #1088 lesson). Values with no form field — the firm's general mailbox and
 * main line, the contact form and profile links, a non-US state and the model's
 * own note — become ONE tidy line that is OFFERED for Notes. Nothing is written
 * to Notes without the specialist's click, and existing notes are never
 * replaced.</p>
 *
 * <p>A firm-level paste (no named person) on a form with no name typed fills
 * Email and Phone from the firm's mailbox and main line: that IS the contact a
 * firm-contact record holds (D19), and leaving them in a note would make a
 * record with nothing to identify it by.</p>
 */

/** The form fields a paste can fill. Notes is deliberately absent: it is only
 *  ever offered. */
export type PasteField =
  | "firstName"
  | "lastName"
  | "email"
  | "phone"
  | "phoneType"
  | "firmName"
  | "title"
  | "city"
  | "state";

export const PASTE_FIELD_LABELS: Record<PasteField, string> = {
  firstName: "First name",
  lastName: "Last name",
  email: "Email",
  phone: "Phone",
  phoneType: "Phone type",
  firmName: "Firm",
  title: "Title",
  city: "City",
  state: "State",
};

export interface PasteConflict {
  field: PasteField;
  /** What the form holds now, as typed. */
  typed: string;
  /** What the paste says. */
  pasted: string;
}

/** "paste" → the small "From paste" marker; "image" → "Read from image — check it". */
export type PasteMarker = "paste" | "image";

export const PASTE_MARKER_TEXT: Record<PasteMarker, string> = {
  paste: "From paste",
  image: "Read from image — check it",
};

export interface PasteMergeResult {
  form: AgentForm;
  /** Fields this merge filled. Each carries a marker until edited. */
  filled: Set<PasteField>;
  conflicts: PasteConflict[];
  /** The one line offered for Notes, or null when there is nothing to offer. */
  notesLine: string | null;
  marker: PasteMarker;
}

const TEXT_FIELDS = [
  "firstName",
  "lastName",
  "email",
  "phone",
  "firmName",
  "title",
  "city",
  "state",
] as const satisfies readonly PasteField[];

type TextField = (typeof TEXT_FIELDS)[number];

const US_STATE_CODES = new Set(US_STATES.map((s) => s.code));

/** The last ten digits — the server's `ListingAgentMatchKeys.Phone` key. */
function phoneKey(value: string): string {
  const digits = value.replace(/[^0-9]/g, "");
  return digits.length >= 10 ? digits.slice(-10) : digits;
}

/** Two values that say the same thing are not a conflict: email ignoring case,
 *  phone by its digits, everything else ignoring case and spacing. */
function sameValue(field: PasteField, a: string, b: string): boolean {
  if (field === "phone") return phoneKey(a) === phoneKey(b);
  const norm = (s: string) => s.trim().replace(/\s+/g, " ").toLowerCase();
  return norm(a) === norm(b);
}

function clean(value: string | null | undefined): string {
  return (value ?? "").trim();
}

/**
 * Merges one pasted person into the form.
 *
 * @param notesLine the extraction's `notesLine` (the model's note), folded into
 *   the offered line after the firm values.
 */
export function mergePasteIntoAgentForm(
  form: AgentForm,
  candidate: ListingAgentPasteCandidateDto,
  { fromImage, notesLine = null }: { fromImage: boolean; notesLine?: string | null },
): PasteMergeResult {
  const next: AgentForm = { ...form };
  const filled = new Set<PasteField>();
  const conflicts: PasteConflict[] = [];
  const offered: string[] = [];

  const firmLevel = !clean(candidate.firstName) && !clean(candidate.lastName);
  const formHasName = Boolean(form.firstName.trim() || form.lastName.trim());
  const routeFirmContact = firmLevel && !formHasName;

  const pasted: Record<TextField, string> = {
    firstName: clean(candidate.firstName),
    lastName: clean(candidate.lastName),
    email: clean(candidate.email),
    phone: clean(candidate.phone),
    firmName: clean(candidate.firmName),
    title: clean(candidate.title),
    city: clean(candidate.city),
    state: clean(candidate.state).toUpperCase(),
  };
  let pastedPhoneType: ListingAgentPhoneType = candidate.phoneType ?? "Unknown";

  const firmEmail = clean(candidate.firmGeneralEmail);
  const firmPhone = clean(candidate.firmMainPhone);
  let firmEmailUsed = false;
  let firmPhoneUsed = false;
  if (routeFirmContact && !pasted.email && firmEmail) {
    pasted.email = firmEmail;
    firmEmailUsed = true;
  }
  if (routeFirmContact && !pasted.phone && firmPhone) {
    pasted.phone = firmPhone;
    firmPhoneUsed = true;
    // A firm's main line is an office line.
    if (pastedPhoneType === "Unknown") pastedPhoneType = "Office";
  }

  if (pasted.state && !US_STATE_CODES.has(pasted.state)) {
    offered.push(`State: ${pasted.state}`);
    pasted.state = "";
  }

  for (const field of TEXT_FIELDS) {
    const value = pasted[field];
    if (!value) continue;
    const typed = form[field];
    if (!typed.trim()) {
      next[field] = value;
      filled.add(field);
    } else if (!sameValue(field, typed, value)) {
      conflicts.push({ field, typed, pasted: value });
    }
  }

  // Phone type follows the phone the form ends up with: filled from the paste,
  // or already the same number. A conflicting phone carries its type with it
  // when the specialist chooses the pasted value (`applyPastedValue`).
  if (pastedPhoneType !== "Unknown" && next.phone.trim() && pasted.phone) {
    const phoneMatches = sameValue("phone", next.phone, pasted.phone);
    if (phoneMatches && form.phoneType === "Unknown") {
      next.phoneType = pastedPhoneType;
      filled.add("phoneType");
    } else if (phoneMatches && form.phoneType !== pastedPhoneType) {
      conflicts.push({ field: "phoneType", typed: form.phoneType, pasted: pastedPhoneType });
    }
  }

  if (firmEmail && !firmEmailUsed && !sameValue("email", firmEmail, next.email)) {
    offered.push(`Firm email: ${firmEmail}`);
  }
  if (firmPhone && !firmPhoneUsed && !sameValue("phone", firmPhone, next.phone)) {
    offered.push(`Firm main line: ${firmPhone}`);
  }
  const contactForm = clean(candidate.contactFormUrl);
  if (contactForm) offered.push(`Contact form: ${contactForm}`);
  const profile = clean(candidate.profileUrl);
  if (profile) offered.push(`Profile: ${profile}`);
  const note = clean(notesLine).replace(/\s+/g, " ");
  if (note) offered.push(note);

  // Anything the notes already say is not offered twice.
  const existingNotes = form.notes.toLowerCase();
  const fresh = offered.filter((part) => !existingNotes.includes(part.toLowerCase()));

  return {
    form: next,
    filled,
    conflicts,
    notesLine: fresh.length > 0 ? fresh.join(" · ") : null,
    marker: fromImage ? "image" : "paste",
  };
}

/**
 * "Use pasted" on one conflict: the field takes the pasted value. Choosing the
 * pasted PHONE also brings the pasted phone type while the form's is still
 * "Unknown", since the type describes that number.
 */
export function applyPastedValue(
  form: AgentForm,
  conflict: PasteConflict,
  pastedPhoneType: ListingAgentPhoneType = "Unknown",
): { form: AgentForm; filled: PasteField[] } {
  if (conflict.field === "phoneType") {
    return { form: { ...form, phoneType: conflict.pasted as ListingAgentPhoneType }, filled: ["phoneType"] };
  }
  const next: AgentForm = { ...form, [conflict.field]: conflict.pasted };
  const filled: PasteField[] = [conflict.field];
  if (conflict.field === "phone" && form.phoneType === "Unknown" && pastedPhoneType !== "Unknown") {
    next.phoneType = pastedPhoneType;
    filled.push("phoneType");
  }
  return { form: next, filled };
}

/** "Add to notes": appends the offered line on its own line, never replacing
 *  what is there, and never adding a line the notes already hold. */
export function appendToNotes(notes: string, line: string): string {
  const addition = line.trim();
  if (!addition) return notes;
  if (!notes.trim()) return addition;
  if (notes.toLowerCase().includes(addition.toLowerCase())) return notes;
  return `${notes.replace(/\s+$/, "")}\n${addition}`;
}

/** The fields whose value changed between two forms — an edit clears their
 *  "from paste" markers. */
export function changedFields(before: AgentForm, after: AgentForm): PasteField[] {
  const fields: PasteField[] = [...TEXT_FIELDS, "phoneType"];
  return fields.filter((f) => before[f] !== after[f]);
}
